import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Error, Hint, Label } from '@progress/kendo-react-labels';
import {
  Editor,
  EditorChangeEvent,
  EditorProps,
  EditorTools,
} from '@progress/kendo-react-editor';

const {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Indent,
  Outdent,
  UnorderedList,
  OrderedList,
  FormatBlock,
  Unlink,
  InsertImage,
  Link,
} = EditorTools;

interface EditorStateWrapperProps<T extends FieldValues>
  extends UseControllerProps<T>, Omit<EditorProps, 'defaultValue' | 'name'> {
  hint?: string;
  label?: string;
  optional?: boolean;
  containerClassName?: string;
}

// ProseMirror (Kendo Editor) wraps list item content in <p> by default.
// Strip those wrappers so the stored HTML contains plain <li>text</li>.
const stripParagraphsInListItems = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('li > p').forEach((p) => {
    p.replaceWith(...Array.from(p.childNodes));
  });
  return div.innerHTML;
};

export const EditorStateWrapper = <T extends FieldValues>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  disabled,
  hint,
  label,
  optional,
  containerClassName,
}: EditorStateWrapperProps<T>) => {
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
    disabled,
  });

  const { onChange, onBlur, value, ...restField } = field;
  const id = field.name;
  const showValidationMessage = Boolean(isTouched && error?.message);
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : '';
  const errorId = showValidationMessage ? `${id}_error` : '';

  return (
    <FieldWrapper
      className={`flex flex-col self-start [&_.k-editor]:!rounded-md [&_.k-toolbar]:!rounded-t-md ${containerClassName || ''}`}
    >
      <Label
        editorId={id}
        editorValid={!showValidationMessage}
        editorDisabled={disabled}
        optional={optional}
        className="k-form-label mb-1"
      >
        {label}
      </Label>
      <div className={'k-form-field-wrap'}>
        <Editor
          value={value ?? ''}
          onChange={(event: EditorChangeEvent) => {
            // Store raw HTML during editing to avoid re-parsing on every keystroke.
            onChange(event?.html ?? '');
          }}
          onBlur={() => {
            // Normalize once when the user leaves the editor.
            onChange(stripParagraphsInListItems(value ?? ''));
            onBlur();
          }}
          tools={[
            [Bold, Italic, Underline, Strikethrough],
            FormatBlock,
            [AlignLeft, AlignCenter, AlignRight, AlignJustify],
            [Indent, Outdent, OrderedList, UnorderedList],
            [Link, Unlink, InsertImage],
          ]}
          contentStyle={{
            height: 200,
          }}
          {...restField}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{error?.message}</Error>}
      </div>
    </FieldWrapper>
  );
};
